import { MessageSquare } from "lucide-react";
import { Card } from "@/components/ui/card";
import { displayCompanyName } from "@/lib/displayCompanyName";
import { useLoadNotes } from "@/hooks/useLoadNotes";

/**
 * Full-width, read-only history of the shared shipper/broker note thread.
 * Posting lives in `AddLoadNoteCard` so the two surfaces never duplicate an
 * input box.
 */
interface Props {
  loadId: string;
  viewerUserId: string;
  viewerRole: "shipper" | "broker";
  cardClassName?: string;
}

function relativeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function LoadNotesHistoryPanel({ loadId, viewerUserId, viewerRole, cardClassName }: Props) {
  const { notes, isLoading } = useLoadNotes(loadId);
  const counterparty = viewerRole === "shipper" ? "broker" : "shipper";

  return (
    <Card className={`p-4 ${cardClassName ?? ""}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-brand-teal" />
          <h3 className="text-sm font-semibold uppercase tracking-wider">Stored Notes</h3>
        </div>
        {notes.length > 0 && (
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {notes.length} {notes.length === 1 ? "note" : "notes"}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="h-16 animate-pulse rounded bg-muted" />
      ) : notes.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">
          No notes yet. Anything you add is visible to the {counterparty}.
        </p>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => {
            const mine = n.author_user_id === viewerUserId;
            const isShipper = n.author_role === "shipper";
            return (
              <div
                key={n.id}
                className={
                  "rounded-md border p-2.5 " +
                  (mine ? "border-brand-teal/30 bg-brand-teal/5" : "border-border bg-muted/30")
                }
              >
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span
                    className={
                      "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide " +
                      (isShipper ? "bg-brand-teal/15 text-brand-teal" : "bg-slate-500/15 text-slate-600")
                    }
                  >
                    {isShipper ? "Shipper" : "Broker"}
                  </span>
                  <span className="text-xs font-medium text-foreground truncate">
                    {displayCompanyName(n.author_company_name) || (isShipper ? "Shipper" : "Broker")}
                  </span>
                  {mine && <span className="text-[10px] text-muted-foreground">(you)</span>}
                  <span className="text-[11px] text-muted-foreground ml-auto">
                    {relativeAgo(n.created_at)}
                  </span>
                </div>
                <p className="text-xs text-foreground whitespace-pre-wrap break-words">{n.body}</p>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export default LoadNotesHistoryPanel;
